import { HERO } from "@/constants/testIds";
import { Leaf, Award, Sprout } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/096b6207-302a-4d30-852c-60e212c167c7/images/1a3cae928d7bd3b84eaaed580ba9bdee7c5c649f179161fa271bab3fdb8671a4.png";

const Badge = ({ icon: Icon, label, testId }) => (
  <div
    data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur-md border border-[#c5a059]/30 px-4 py-2 text-[12px] tracking-wide text-[#1a3626] shadow-[0_4px_18px_rgba(26,54,38,0.06)]"
  >
    <Icon size={14} className="text-[#8b5a2b]" />
    {label}
  </div>
);

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid={HERO.wrapper}
      className="relative pt-32 sm:pt-40 lg:pt-44 pb-20 sm:pb-28 overflow-hidden"
    >
      {/* Decorative cashew/leaf accents */}
      <div className="pointer-events-none absolute -top-20 -left-24 w-[400px] h-[400px] rounded-full bg-[#c5a059]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 right-0 w-[500px] h-[500px] rounded-full bg-[#c86b5e]/8 blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left content */}
        <div className="lg:col-span-7 relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#c5a059]" />
            <span className="overline">Natural · Nutritious · Nigerian</span>
          </div>

          <h1
            data-testid={HERO.headline}
            className="font-serif-display text-[44px] sm:text-[60px] lg:text-[78px] leading-[1.02] text-[#1a3626] tracking-tight"
          >
            Premium Cashews from{" "}
            <em className="not-italic relative inline-block">
              <span className="text-[#8b5a2b] italic">Nigerian Farms</span>
              <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-[#c5a059]/60 rounded-full" />
            </em>{" "}
            to Your Table
          </h1>

          <p
            data-testid={HERO.subheadline}
            className="mt-7 text-[16px] sm:text-[18px] leading-[1.7] text-[#5c4033] max-w-xl"
          >
            Naturally grown, carefully selected and packed for a new generation
            of conscious snack lovers. Single-origin, small-batch and slow-roasted.
          </p>

          <div className="mt-9 flex flex-wrap gap-3">
            <Badge
              icon={Leaf}
              label="Nigeria Origin"
              testId={HERO.badgeOrigin}
            />
            <Badge
              icon={Award}
              label="Premium Quality"
              testId={HERO.badgeQuality}
            />
            <Badge
              icon={Sprout}
              label="Small Batch"
              testId={HERO.badgeBatch}
            />
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              data-testid={HERO.ctaPrimary}
              href="#story"
              className="inline-flex items-center justify-center gap-2 bg-[#1a3626] text-[#faf7f2] rounded-full px-7 py-4 text-[14px] font-medium hover:bg-[#12261b] transition-all duration-300 shadow-[0_10px_30px_rgba(26,54,38,0.22)] hover:-translate-y-0.5"
            >
              Discover the Story
              <span className="text-[#c5a059]">→</span>
            </a>
            <a
              data-testid={HERO.ctaSecondary}
              href="#signup"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-[#1a3626] border border-[#1a3626]/25 rounded-full px-7 py-4 text-[14px] font-medium hover:border-[#1a3626] hover:bg-[#1a3626]/5 transition-all duration-300"
            >
              Join Launch List
            </a>
          </div>
        </div>

        {/* Right product visual */}
        <div className="lg:col-span-5 relative">
          <div className="relative">
            {/* Decorative ring */}
            <div className="absolute -inset-6 rounded-[3rem] border border-[#c5a059]/30" />
            <div className="absolute -inset-2 rounded-[3rem] border border-[#c5a059]/15" />
            <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-[#fdf9f8] shadow-[0_30px_80px_rgba(26,54,38,0.18)]">
              <img
                src={HERO_IMG}
                alt="Premium NAIJA Cashews pouch on warm cream surface"
                className="w-full h-full object-cover scale-[1.18] transition-transform duration-[1200ms] hover:scale-[1.25]"
              />
              {/* Floating tag */}
              <div className="absolute top-5 left-5 bg-[#1a3626] text-[#faf7f2] px-4 py-2 rounded-full text-[11px] tracking-[0.18em] uppercase flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#c5a059]" />
                Launching Soon
              </div>
              {/* Floating price card */}
              <div className="absolute bottom-5 right-5 bg-[#faf7f2]/95 backdrop-blur-md rounded-2xl px-5 py-3 shadow-[0_10px_30px_rgba(26,54,38,0.18)] border border-[#c5a059]/25">
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#8b5a2b]">
                  Single Origin
                </div>
                <div className="font-serif-display text-[20px] text-[#1a3626] leading-none mt-1">
                  Naija 250g
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="mt-20 sm:mt-24 border-y border-[#1a3626]/8 py-5 overflow-hidden">
        <div className="flex marquee-track gap-16 whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex gap-16 items-center shrink-0">
              {[
                "Single Origin · Nigeria",
                "Slow-roasted, Small batch",
                "No additives. Ever.",
                "Crafted for Europe",
                "Fair value at the source",
                "Premium D2C",
              ].map((t, i) => (
                <span
                  key={i}
                  className="text-[11px] tracking-[0.28em] uppercase text-[#1a3626]/60 flex items-center gap-16"
                >
                  {t}
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c5a059]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
